import Chart from 'chart.js/auto'
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import trendlinePlugin from 'chartjs-plugin-trendline';

Chart.register(zoomPlugin, annotationPlugin, trendlinePlugin);

const CHART_COLORS = {
  red: 'rgb(255, 99, 132)',
  blue: 'rgb(54, 162, 235)',
  green: 'rgb(75, 192, 192)',
  night: 'rgba(54, 162, 235, 0.2)',
};

const labels = [];
const datapoints = [];
const data = {};

async function loadPendingJobsData() {
  const response = await fetch('/pending_jobs.txt');
  const text = await response.text();
  const lines = text.trim().split('\n').slice(1); // skip header

  const labels = [];
  const datapoints = [];

  for (const line of lines) {
    const [pending, date] = line.split(';');
    labels.push(date + "Z"); // Append "Z" to indicate UTC time
    datapoints.push(parseInt(pending, 10));
  }

  return { labels, datapoints };
}

function generateNightAnnotations(labels, yMax) {
  const annotations = {};
  let nightStartIdx = null;

  for (let i = 0; i < labels.length; i++) {
    const date = new Date(labels[i] + 'Z');
    const hour = date.getUTCHours();
    const isNight = hour >= 20 || hour < 6;

    if (isNight && nightStartIdx === null) {
      nightStartIdx = i; // Start of night period
    } else if (!isNight && nightStartIdx !== null) {
      // End of night period - create annotation
      const name = `night_${nightStartIdx}_${i}`;
      annotations[name] = {
        type: 'box',
        xMin: labels[nightStartIdx],
        xMax: labels[i - 1],
        yMin: 0,
        yMax: yMax,
        backgroundColor: 'rgba(0, 0, 100, 0.1)',
        borderColor: 'rgba(0, 0, 200, 0.3)',
        borderWidth: 0,
        z: -1, // Place behind chart data
        animation: false,
      };
      nightStartIdx = null;
    }
  }

  // Handle case where data ends during night
  if (nightStartIdx !== null) {
    const name = `night_${nightStartIdx}_${labels.length - 1}`;
    annotations[name] = {
      type: 'box',
      xMin: labels[nightStartIdx],
      xMax: labels[labels.length - 1],
      yMin: 0,
      yMax: yMax,
      backgroundColor: 'rgba(0, 0, 100, 0.1)',
      borderColor: 'rgba(0, 0, 200, 0.3)',
      borderWidth: 0,
      z: -1,
      animation: false,
    };
  }

  return annotations;
}

(async function() {
  const { labels, datapoints } = await loadPendingJobsData();

  // Find date range across all data
  const allDates = [...labels].map(d => new Date(d.endsWith('Z') ? d : d + 'Z'));
  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

  // Generate 4-minute interval labels
  const intervalMs = 4 * 60000;
  const intervalLabels = [];
  for (let t = minDate.getTime(); t <= maxDate.getTime(); t += intervalMs) {
    intervalLabels.push(new Date(t).toISOString().replace('Z', ''));
  }

  // Helper to get bucket index for a timestamp
  const getBucketIndex = (ts) => {
    const date = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return Math.floor((date.getTime() - minDate.getTime()) / intervalMs);
  };

  // Initialize buckets with -Infinity and track if data exists
  const pendingBuckets = Array(intervalLabels.length).fill(-Infinity);
  const pendingHasData = Array(intervalLabels.length).fill(false);

  // Fill pending jobs buckets with max values
  for (let i = 0; i < labels.length; i++) {
    const bucketIdx = getBucketIndex(labels[i]);
    if (bucketIdx >= 0 && bucketIdx < pendingBuckets.length) {
      pendingBuckets[bucketIdx] = Math.max(pendingBuckets[bucketIdx], datapoints[i]);
      pendingHasData[bucketIdx] = true;
    }
  }

  // Convert to final datapoint arrays (NaN where no data)
  const mergedPendingDatapoints = pendingBuckets.map((v, i) => pendingHasData[i] ? v : NaN);

  const yMax = Math.max(...mergedPendingDatapoints.filter(v => !isNaN(v)), 10) + 10;

  const data = {
    labels: intervalLabels,
    datasets: [
      {
        label: 'Pending Jobs',
        data: mergedPendingDatapoints,
        borderColor: CHART_COLORS.red,
        fill: false,
        tension: 0.1,

        trendlineLinear: {
            colorMin: '#3e95cd',
            width: 2,
            lineStyle: 'dotted',
        },

      }
    ]
  };

  const config = {
    type: 'line',
    data: data,
    options: {
      animation: false,
      responsive: true,

      elements: {
        point: {
          pointStyle: false
        }
      },

      transitions: {
        zoom: {
          animation: {
            duration: 0
          }
        }
      },

      plugins: {
        title: {
          display: true,
          text: 'Pending Jobs Over Time'
        },

        zoom: {
          pan: {
            enabled: true,
            mode: 'x',
            modifierKey: 'ctrl',
          },
          zoom: {
            wheel: {
              enabled: true
            },
            drag: {
              enabled: true
            },
            mode: 'x',
          },
        },

        annotation: {
          annotations: generateNightAnnotations(intervalLabels, yMax)
        },

      },

      interaction: {
        intersect: false,
      },

      scales: {
        x: {
          display: true,
          title: {
            display: true
          },
          // type: 'timeseries',
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Value'
          },
          suggestedMin: 0,
          suggestedMax: 10
        }
      },
    }
  };

  new Chart(
    document.getElementById('acquisitions'),
    config
  );
})();
